import type { PermissionLevel } from "./AuthProvider";

type HasPermission = (moduleId: string, level?: PermissionLevel) => boolean;

type RouteRequirement = {
  moduleId: string;
  level?: PermissionLevel;
  anyOf?: RouteRequirement[];
};

const publicPaths = ["/activate", "/privacy", "/login", "/beta", "/app-beta"];
const authOnlyPaths = ["/", "/help"];

const routeRequirements: Array<{ match: (path: string) => boolean; requirement: RouteRequirement }> = [
  { match: (path) => path === "/calendar", requirement: { moduleId: "calendar" } },
  { match: (path) => path === "/news", requirement: { moduleId: "news" } },
  { match: (path) => path === "/messaging", requirement: { moduleId: "messaging" } },
  { match: (path) => path === "/messaging/new-direct", requirement: { moduleId: "messaging", level: "edit" } },
  { match: (path) => path === "/messaging/new-staff", requirement: { moduleId: "messaging", level: "edit" } },
  { match: (path) => path === "/messaging/new-announcement", requirement: { moduleId: "messaging", level: "edit" } },
  { match: (path) => path === "/messaging/history", requirement: { moduleId: "messageHistory" } },
  { match: (path) => path === "/messaging/truncate", requirement: { moduleId: "superAdminAccess", level: "manage" } },
  { match: (path) => path === "/messaging/inbox-tools", requirement: { moduleId: "superAdminAccess", level: "manage" } },
  { match: (path) => path === "/messaging/thread-debug", requirement: { moduleId: "superAdminAccess", level: "manage" } },
  { match: (path) => path === "/audit-logs", requirement: { moduleId: "auditLogs" } },
  { match: (path) => path === "/settings", requirement: { moduleId: "settings" } },
  { match: (path) => path === "/school-year", requirement: { moduleId: "schoolYear" } },
  { match: (path) => path === "/developer", requirement: { moduleId: "developerPortal" } },
  { match: (path) => path === "/volunteer/review", requirement: { moduleId: "volunteerReview" } },
  { match: (path) => path === "/volunteer/operations", requirement: { moduleId: "volunteerOps" } },
  { match: (path) => path === "/volunteer/settings", requirement: { moduleId: "volunteerSettings" } },
  { match: (path) => path === "/technology", requirement: { moduleId: "techInventory" } },
  { match: (path) => path === "/users", requirement: { moduleId: "users" } },
  { match: (path) => path === "/access-roles", requirement: { moduleId: "accessControl" } },
  { match: (path) => path === "/bootstrap", requirement: { moduleId: "superAdminAccess", level: "manage" } },
  { match: (path) => path === "/roster/import", requirement: { moduleId: "roster", level: "manage" } },
  { match: (path) => path === "/clubs" || path.startsWith("/clubs/"), requirement: { moduleId: "clubs" } },
  { match: (path) => path === "/ptu" || path.startsWith("/ptu/"), requirement: { moduleId: "ptuSettings", anyOf: [{ moduleId: "ptuSettings" }, { moduleId: "ptuMessaging" }, { moduleId: "ptuSignups" }] } },
  { match: (path) => path === "/roster" || path.startsWith("/roster/"), requirement: { moduleId: "roster" } },
  { match: (path) => path === "/families", requirement: { moduleId: "families" } },
  { match: (path) => path === "/family-crm", requirement: { moduleId: "familyCrm" } },
  { match: (path) => path === "/public-web" || path === "/public-web/pages", requirement: { moduleId: "publicWebPages" } },
  { match: (path) => path === "/public-web/theme", requirement: { moduleId: "publicWebTheme" } },
  { match: (path) => path === "/public-web/forms", requirement: { moduleId: "publicWebForms" } },
  { match: (path) => path === "/public-web/announcements" || path === "/public-web/newsletters", requirement: { moduleId: "publicWebPages" } },
  { match: (path) => path === "/public-web/donations", requirement: { moduleId: "donorCrm" } },
  { match: (path) => path === "/public-web/inquiries", requirement: { moduleId: "admissionsCrm" } },
  { match: (path) => path === "/public-web/activity" || path === "/public-web/launch-checklist", requirement: { moduleId: "publicWebActivity" } },
  { match: (path) => path === "/public-web/analytics", requirement: { moduleId: "publicWebAnalytics" } },
  { match: (path) => path === "/public-web/staff" || path === "/public-web/board" || path === "/public-web/ptu" || path === "/public-staff", requirement: { moduleId: "publicWebPeople" } },
  { match: (path) => path === "/data/purge", requirement: { moduleId: "dataPurge", level: "manage" } },
];

function normalizePath(path: string) {
  return `/${String(path || "/").replace(/^\/+/, "").split("?")[0].split("#")[0]}`.replace(/\/+$/, "") || "/";
}

export function homePathForPermissions(hasPermission: HasPermission) {
  if (hasPermission("dashboard", "view")) return "/";
  if (hasPermission("publicWebPages", "view")) return "/public-web/pages";
  if (hasPermission("developerPortal", "view")) return "/developer";
  if (hasPermission("news", "view")) return "/news";
  return "/";
}

export function canAccessRoute(path: string, hasPermission: HasPermission) {
  const normalized = normalizePath(path);
  if (publicPaths.includes(normalized) || authOnlyPaths.includes(normalized)) return true;

  const route = routeRequirements.find((item) => item.match(normalized));
  if (!route) return false;

  if (route.requirement.anyOf?.length) {
    return route.requirement.anyOf.some((requirement) =>
      hasPermission(requirement.moduleId, requirement.level || "view")
    );
  }

  return hasPermission(route.requirement.moduleId, route.requirement.level || "view");
}
